import { useRef, useEffect, useState, useCallback } from "react";
import Modal from '../../../components/Modal';
import { useModal } from '../../../hooks/useModal';
import { useToastContext } from '../../../App';
import { IMAGE_CONVERT_ERROR_MESSAGE } from '../../../utils/prepareImageFileForUpload';
import { formFieldClass, formShell } from '../../../styles/formShell';
import { mediaShell } from '../../../styles/mediaShell';

function MediaUploadDropZone({ onFilesAdded, mediaList, maxFiles = 10 }) {
    const fileInputRef = useRef(null);
    const [error, setError] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const { showToast } = useToastContext();

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        setError(null);
        if (isConverting) return;
        setIsConverting(true);
        try {
            await onFilesAdded(e.dataTransfer.files);
        } catch (err) {
            const msg = err?.message || IMAGE_CONVERT_ERROR_MESSAGE;
            setError(msg);
            showToast(msg, 'error');
        } finally {
            setIsConverting(false);
        }
    };

    const handleInputChange = async (e) => {
        setError(null);
        if (isConverting) return;
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setIsConverting(true);
        try {
            await onFilesAdded(files);
        } catch (err) {
            const msg = err?.message || IMAGE_CONVERT_ERROR_MESSAGE;
            setError(msg);
            showToast(msg, 'error');
        } finally {
            setIsConverting(false);
        }
    };

    const currentCount = Array.isArray(mediaList) ? mediaList.length : 0;
    const dropZoneMessage = "メディアを追加";
    const dropClass = isConverting
        ? formShell.dropzoneDisabled
        : isDragging
          ? formShell.dropzoneActive
          : formShell.dropzone;

    return (
        <div className="w-full min-w-0 shrink-0">
            <div
                className={dropClass}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!isConverting) setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isConverting) fileInputRef.current.click();
                }}
                aria-busy={isConverting}
            >
                <p className="m-0">{isConverting ? '画像を変換しています…' : dropZoneMessage}</p>
                <p className={`${formShell.hint} mt-2`}>
                    {currentCount}/{maxFiles} ファイル | 画像:10MB 動画:50MB 音声:20MB
                </p>
                <input
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*"
                    ref={fileInputRef}
                    onChange={handleInputChange}
                    className="hidden"
                />
            </div>
            {error && (
                <div className="mt-2 rounded-md border border-solid border-error-border-soft bg-error-bg p-2">
                    <p className={`${formShell.error}`}>{error}</p>
                </div>
            )}
        </div>
    );
}

function MediaPreviewItem({ media, idx, removeFileAt, updateFileMeta, deleteMedia, markerId }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const blobUrlRef = useRef(null);
    const { isOpen, openModal, closeModal } = useModal();

    const revokeBlobUrl = useCallback((url) => {
        if (url) {
            try {
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Failed to revoke blob URL:', error);
            }
        }
    }, []);

    useEffect(() => {
        if (media.url) {
            if (blobUrlRef.current) {
                revokeBlobUrl(blobUrlRef.current);
                blobUrlRef.current = null;
                setBlobUrl(null);
            }
            return;
        }

        if (media.file) {
            try {
                if (blobUrlRef.current) {
                    revokeBlobUrl(blobUrlRef.current);
                }

                const newBlobUrl = URL.createObjectURL(media.file);
                blobUrlRef.current = newBlobUrl;
                setBlobUrl(newBlobUrl);
            } catch (error) {
                console.error('Failed to create blob URL:', error);
                blobUrlRef.current = null;
                setBlobUrl(null);
            }
        } else {
            if (blobUrlRef.current) {
                revokeBlobUrl(blobUrlRef.current);
                blobUrlRef.current = null;
                setBlobUrl(null);
            }
        }

        return () => {
            if (blobUrlRef.current) {
                revokeBlobUrl(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [media.url, media.file, revokeBlobUrl]);

    const previewUrl = media.url || blobUrl;
    // 追加直後から未入力なら警告（フォーカス不要）
    const showNameError = !media.name?.trim();

    const renderPreviewContent = () => {
        if (!previewUrl) {
            return <p>プレビューを読み込めません</p>;
        }

        const type = media.media_type;

        try {
            if (type.startsWith("image")) {
                return (
                    <img
                        src={previewUrl}
                        alt={media.name}
                        onError={(e) => {
                            console.error('Failed to load image:', e);
                            e.target.style.display = 'none';
                        }}
                    />
                );
            } else if (type.startsWith("video")) {
                return (
                    <video
                        src={previewUrl}
                        controls
                        onError={(e) => {
                            console.error('Failed to load video:', e);
                        }}
                    />
                );
            } else if (type.startsWith("audio")) {
                return (
                    <audio
                        src={previewUrl}
                        controls
                        onError={(e) => {
                            console.error('Failed to load audio:', e);
                        }}
                    />
                );
            } else {
                return <p>未対応のメディアタイプ</p>;
            }
        } catch (error) {
            console.error('Error rendering media preview:', error);
            return <p>プレビューを表示できません</p>;
        }
    };

    const handleDeleteClick = () => {
        openModal();
    };

    const handleNameChange = (value) => {
        updateFileMeta(idx, "name", value);
    };

    const handleDescriptionChange = (value) => {
        updateFileMeta(idx, "description", value);
    };

    const handleConfirmDelete = () => {
        closeModal();

        if (media.url && deleteMedia && markerId) {
            deleteMedia(media.id, markerId);
        } else if (deleteMedia && markerId) {
            deleteMedia(media.id || `new_${idx}`, markerId, true, idx);
        } else {
            removeFileAt(idx);
        }
    };

    return (
        <>
            <div className={mediaShell.selectedEntry}>
                <button
                    className={mediaShell.removeButton}
                    onClick={handleDeleteClick}
                    aria-label="メディアを削除"
                    type="button"
                >
                    ✕
                </button>

                <div className={mediaShell.selectedPreview}>
                    {renderPreviewContent()}
                </div>

                <div className={mediaShell.selectedContent}>
                    <div className={mediaShell.selectedFieldGroup}>
                        <label className={formShell.label}>タイトル</label>
                        <input
                            type="text"
                            value={media.name || ""}
                            onChange={(e) => handleNameChange(e.target.value)}
                            className={`${formFieldClass(showNameError)} ${mediaShell.selectedTitleInput}`}
                            placeholder="例：〇〇祭りの写真"
                            aria-invalid={showNameError}
                            aria-describedby={`media-title-error-${idx}`}
                        />
                        <p
                            id={`media-title-error-${idx}`}
                            className={`${formShell.error} ${mediaShell.errorSlot} ${showNameError ? "" : "invisible"}`}
                            role={showNameError ? "alert" : undefined}
                        >
                            タイトルは必須です
                        </p>
                    </div>

                    <div className={mediaShell.selectedFieldGroup}>
                        <label className={formShell.label}>説明文</label>
                        <textarea
                            value={media.description || ""}
                            onChange={(e) => handleDescriptionChange(e.target.value)}
                            className={`${formFieldClass(false, 'textarea')} ${mediaShell.selectedDescInput}`}
                            rows={4}
                            placeholder="例：〇〇祭りの様子です。"
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="メディアを削除"
                size="small"
                actions={[
                    { label: "キャンセル", variant: "secondary" },
                    { label: "削除", variant: "danger", onClick: handleConfirmDelete }
                ]}
            >
                <p>この操作は取り消せません。</p>
            </Modal>
        </>
    );
}

function MediaPreviewList({ mediaList, removeFileAt, updateFileMeta, deleteMedia, markerId }) {
    if (!Array.isArray(mediaList) || mediaList.length === 0) {
        return (
            <p className={mediaShell.emptyState}>
                メディアが登録されていません。
            </p>
        );
    }

    return (
        <div className={mediaShell.selectedList}>
            {mediaList.map((media, idx) => {
                const uniqueKey = media.id
                    ? `existing-${media.id}`
                    : `new-${idx}-${media.file?.name || 'unknown'}`;

                return (
                    <MediaPreviewItem
                        key={uniqueKey}
                        media={media}
                        idx={idx}
                        removeFileAt={removeFileAt}
                        updateFileMeta={updateFileMeta}
                        deleteMedia={deleteMedia}
                        markerId={markerId}
                    />
                );
            })}
        </div>
    );
}

export function MediaUploadManager({
    mediaList,
    addMedia,
    uploadMedia,
    deleteMedia,
    removeFileAt,
    maxFiles = 10,
    markerId,
}) {
    return (
        <div className={mediaShell.uploadForm}>
            <MediaUploadDropZone
                onFilesAdded={addMedia}
                mediaList={mediaList}
                maxFiles={maxFiles}
            />

            <MediaPreviewList
                mediaList={mediaList}
                removeFileAt={removeFileAt}
                updateFileMeta={uploadMedia}
                deleteMedia={deleteMedia}
                markerId={markerId}
            />
        </div>
    );
}
